import warningIcon from "$assets/icons/warning-icon.svg?url";
import { Button } from "$components/button/button";
import { DialogBody } from "$components/dialog/dialog-body";
import { DialogContainer } from "$components/dialog/dialog-container";
import { DialogFooter } from "$components/dialog/dialog-footer";
import { DialogHeader } from "$components/dialog/dialog-header";
import { TextField } from "$components/rhf-fields";
import type { Invoice as RxInvoice } from "$global-providers/rxdb-provider/collections/invoice";
import {
  isServerTimeUnknownError,
  serverTimeUnknownMessage,
  useServerTime,
} from "$global-providers/server-time-provider/provider";
import { toast } from "$global-providers/toast-provider";
import { useUser } from "$global-providers/user-provider";
import { randomUniqueId } from "$routes/_lib/random-id";
import {
  useConvertFromPenceFactory,
  useConvertFromPoundsFactory,
} from "$routes/_lib/use-convert-currency";
import useCurrencyFormatter from "$routes/patient/[:patientId]/_lib/invoices/hooks/useCurrencyFormatter";
import { updateInvoiceDocument } from "$routes/patient/[:patientId]/_lib/invoices-v2/update-invoice-document";
import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Grid, makeStyles, Typography } from "@material-ui/core";
import CheckIcon from "@material-ui/icons/Check";
import {
  PayerType,
  PaymentMethod,
  TransactionStatus,
  TransactionType,
} from "elephant-web/gql";
import { useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import type { RxDocument } from "rxdb";
import { number, object, string } from "yup";

const REASON = "reason";
const AMOUNT_TO_REFUND = "amountToRefund";

const useStyles = makeStyles((theme) => ({
  totalAmount: {
    marginTop: "4px",
    fontSize: "24px",
    fontWeight: 700,
  },
  warningMessage: {
    color: theme.palette.grey[600],
    fontWeight: 400,
    marginLeft: theme.spacing(0.5),
  },
  warningIcon: { marginRight: theme.spacing(2) },
}));

type FormState = { reason: string; amountToRefund: number };

/**
 * V2 refund modal. Appends a REFUND transaction to `doc.transactions`
 * via `doc.patch(...)`. Replication carries the new transaction array
 * to the backend on next push.
 */
export const RefundInvoiceModalV2 = ({
  doc,
  open,
  onClose,
  currency,
  totalRefundableInPence,
}: {
  doc: RxDocument<RxInvoice>;
  open: boolean;
  onClose: () => void;
  currency: string;
  totalRefundableInPence: number;
}) => {
  const classes = useStyles();
  const { formatMessage } = useIntl();
  const { currencyFormatter } = useCurrencyFormatter();
  const penceToPounds = useConvertFromPenceFactory();
  const poundsToPence = useConvertFromPoundsFactory();
  const { getCurrentTime } = useServerTime();
  const user = useUser();
  const [busy, setBusy] = useState(false);

  const validationSchema = useMemo(
    () =>
      object().shape({
        [REASON]: string().required(),
        [AMOUNT_TO_REFUND]: number()
          .required()
          .positive()
          .max(penceToPounds(totalRefundableInPence)),
      }),
    [totalRefundableInPence, penceToPounds],
  );

  const formMethods = useForm<FormState>({
    mode: "onTouched",
    resolver: yupResolver(validationSchema),
  });

  const handleErrorMessage = (value) =>
    value?.id ? formatMessage(value) : value;

  const handleRefund = async (values: FormState) => {
    setBusy(true);
    try {
      const timestamp = getCurrentTime().toISOString();
      const refundInPence = poundsToPence(values.amountToRefund);
      const refundTransactionId = randomUniqueId();
      const voidTransactionId = randomUniqueId();
      await updateInvoiceDocument(doc, {
        actionedByUserId: user.id,
        timestamp,
        update: (invoice) => {
          const current = invoice.toRecord();
          const patient = current.payers.find(
            (payer) => payer.type === PayerType.Patient,
          ) as (typeof current.payers)[number];
          return invoice
            .refund({
              transaction: {
                id: refundTransactionId,
                uuid: null,
                type: TransactionType.Refund,
                status: TransactionStatus.Success,
                timestamp,
                actionedByUserId: user.id,
                amount: {
                  value: -refundInPence,
                  currency: current.balance.currency,
                },
                refundReason: values.reason,
                refundPaymentMethod: PaymentMethod.Cash,
                payerName: patient.name,
                payerType: PayerType.Patient,
              },
              ...(patient.balance.due > 0
                ? {
                    voidTransaction: {
                      id: voidTransactionId,
                      uuid: null,
                      type: TransactionType.Void,
                      status: TransactionStatus.Success,
                      timestamp,
                      actionedByUserId: user.id,
                      notes: values.reason,
                      payerName: patient.name,
                      payerType: PayerType.Patient,
                    },
                  }
                : {}),
            })
            .toRecord();
        },
      });
    } catch (err) {
      if (isServerTimeUnknownError(err)) {
        toast.error(formatMessage(serverTimeUnknownMessage));
      } else {
        toast.error(
          formatMessage({
            id: "invoicing.refundInvoice.error.msg",
            defaultMessage: "Failed to refund this invoice",
          }),
        );
      }
    } finally {
      setBusy(false);
      onClose();
    }
  };

  return (
    <DialogContainer open={open} onClose={onClose}>
      <DialogHeader title="">
        <Box display="flex" alignItems="center">
          <img
            src={warningIcon}
            alt="warning"
            className={classes.warningIcon}
          />
          <Typography variant="h5">
            <FormattedMessage
              id="invoicing.refund.modal.title"
              defaultMessage="Refund this invoice"
            />
          </Typography>
        </Box>
      </DialogHeader>
      <FormProvider {...formMethods}>
        <form
          name="refund-invoice-form"
          onSubmit={formMethods.handleSubmit(handleRefund)}
        >
          <DialogBody>
            <Grid container>
              <Grid item xs={12}>
                <Typography variant="caption">
                  <FormattedMessage
                    id="invoicing.refund.totalRefundable"
                    defaultMessage="Total refundable"
                  />
                </Typography>
                <Typography className={classes.totalAmount}>
                  {currencyFormatter(totalRefundableInPence)}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  name={AMOUNT_TO_REFUND}
                  label={formatMessage(
                    {
                      id: "invoicing.refund.amount.label",
                      defaultMessage: "Amount to refund ({currency})",
                    },
                    { currency },
                  )}
                  required
                  fullWidth
                  type="number"
                  disabled={busy}
                  helperText={
                    formMethods.formState.touchedFields[AMOUNT_TO_REFUND] &&
                    handleErrorMessage(
                      formMethods.formState.errors[AMOUNT_TO_REFUND],
                    )
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  name={REASON}
                  label={formatMessage({
                    id: "invoicing.refund.reason.label",
                    defaultMessage: "Reason for refund",
                  })}
                  required
                  multiline
                  minRows={3}
                  fullWidth
                  disabled={busy}
                  helperText={
                    formMethods.formState.touchedFields[REASON] &&
                    handleErrorMessage(formMethods.formState.errors[REASON])
                  }
                />
              </Grid>
            </Grid>
          </DialogBody>
          <DialogFooter>
            <Box>
              <Button color="secondary" onClick={onClose} disabled={busy}>
                <FormattedMessage
                  id="invoicing.refund.cancel"
                  defaultMessage="Cancel"
                />
              </Button>
              <Button
                type="submit"
                color="primary"
                data-testid="invoicing.refund.accept"
                startIcon={<CheckIcon />}
                disabled={
                  formMethods.formState.isSubmitting ||
                  !formMethods.formState.isValid
                }
                loading={busy}
              >
                <FormattedMessage
                  id="invoicing.refund.confirm"
                  defaultMessage="Refund"
                />
              </Button>
            </Box>
          </DialogFooter>
        </form>
      </FormProvider>
    </DialogContainer>
  );
};
